import React, { useRef } from "react";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import {
  useThemeStore,
  ThemePreference,
} from "components/main/stores/themeStore";
import { Tooltip } from "components/common/Tooltip";

const OPTIONS: {
  value: ThemePreference;
  label: string;
  Icon: typeof LightModeOutlinedIcon;
}[] = [
  { value: "light", label: "Light", Icon: LightModeOutlinedIcon },
  { value: "dark", label: "Dark", Icon: DarkModeOutlinedIcon },
  { value: "system", label: "System", Icon: DesktopWindowsOutlinedIcon },
];

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

const systemTheme = (): string =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

/// The page cross-fades into the new theme while the picker itself, captured
/// on its own (`view-transition-name`), switches at once and slides its thumb.
const switchTheme = (apply: () => void) => {
  if (typeof document.startViewTransition !== "function") return apply();
  if (prefersReducedMotion()) return apply();
  document.startViewTransition(apply);
};

export const ThemePicker: React.FC = () => {
  const { preference, setPreference } = useThemeStore();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const index = OPTIONS.findIndex((o) => o.value === preference);

  const choose = (next: ThemePreference) => {
    if (next !== preference) switchTheme(() => setPreference(next));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    e.preventDefault();
    const next = (index + step + OPTIONS.length) % OPTIONS.length;
    choose(OPTIONS[next].value);
    buttons.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="theme-picker"
      style={{ "--theme-index": index } as React.CSSProperties}
      onKeyDown={onKeyDown}
    >
      <span className="theme-picker-thumb" aria-hidden />
      {OPTIONS.map(({ value, label, Icon }, i) => (
        <Tooltip
          key={value}
          title={value === "system" ? `${label} · ${systemTheme()} now` : label}
          placement="bottom"
        >
          <button
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={value === preference}
            aria-label={label}
            tabIndex={value === preference ? 0 : -1}
            className="theme-picker-option"
            onClick={() => choose(value)}
          >
            <Icon sx={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      ))}
    </div>
  );
};
